import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { Instagram, Youtube, Twitter, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useSettings } from "@/contexts/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay }} className={className}>
      {children}
    </motion.div>
  );
}

function InfluencerCard({ influencer }) {
  return (
    <div className="bg-[#161618] border border-white/5 rounded-xl overflow-hidden hover:border-primary/20 transition-all group" data-testid={`influencer-card-${influencer.id}`}>
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        {influencer.image_url ? (
          <img
            src={influencer.image_url.startsWith("http") ? influencer.image_url : `${process.env.REACT_APP_BACKEND_URL}${influencer.image_url}`}
            alt={influencer.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
        ) : null}
        <div className={`${influencer.image_url ? "hidden" : "flex"} w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 items-center justify-center`}>
          <span className="text-5xl font-bold text-primary/50 font-['Zain']">{influencer.name?.charAt(0)}</span>
        </div>
        <div className="absolute top-3 right-3">
          <Badge className="bg-black/60 backdrop-blur-sm text-white text-xs border-0">{influencer.category}</Badge>
        </div>
        {influencer.followers_count && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1">
            <Users size={12} className="text-primary" />
            <span className="text-white text-xs font-medium">{influencer.followers_count}</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-5">
        <h3 className="text-lg font-bold font-['Zain'] text-white mb-1">{influencer.name}</h3>
        {influencer.bio && <p className="text-zinc-400 text-sm leading-relaxed mb-4 line-clamp-2">{influencer.bio}</p>}

        {/* Social Links */}
        <div className="flex items-center gap-2">
          {influencer.instagram && (
            <a href={influencer.instagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-[#E4405F] hover:bg-[#E4405F]/10 transition-all" data-testid={`influencer-ig-${influencer.id}`}>
              <Instagram size={15} />
            </a>
          )}
          {influencer.tiktok && (
            <a href={influencer.tiktok} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-all" data-testid={`influencer-tt-${influencer.id}`}>
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-[15px] h-[15px]"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.48V13.1a8.2 8.2 0 005.58 2.17v-3.44a4.85 4.85 0 01-2-.71v-4.43z" /></svg>
            </a>
          )}
          {influencer.youtube && (
            <a href={influencer.youtube} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-[#FF0000] hover:bg-[#FF0000]/10 transition-all" data-testid={`influencer-yt-${influencer.id}`}>
              <Youtube size={15} />
            </a>
          )}
          {influencer.snapchat && (
            <a href={influencer.snapchat} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-[#FFFC00] hover:bg-[#FFFC00]/10 transition-all" data-testid={`influencer-sc-${influencer.id}`}>
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-[15px] h-[15px]"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12.921-.236.126-.06.27-.09.42-.09.21 0 .405.06.57.18.33.21.435.615.36.975-.12.495-.585.84-1.11 1.02a4.6 4.6 0 01-.63.15c-.135.03-.27.06-.405.105-.21.075-.435.24-.465.54-.03.33.165.63.375.855 1.11 1.23 2.174 2.205 3.194 2.88.285.18.51.33.585.39.254.168.443.378.583.586l.06.1c.21.42.075.87-.12 1.095-.255.285-.66.45-1.17.48-.36.03-.72-.015-1.08-.06l-.12-.015c-.36-.045-.705-.105-1.035-.12-.42-.015-.78.045-1.065.195-.285.15-.51.375-.705.6a5.5 5.5 0 01-.12.15c-.405.48-.855.925-1.65 1.215-.825.3-1.59.405-2.715.405-1.08 0-1.86-.09-2.685-.39-.81-.3-1.245-.72-1.68-1.215-.03-.045-.06-.09-.105-.15-.195-.225-.42-.45-.705-.6-.285-.15-.645-.21-1.065-.195-.33.015-.675.075-1.035.12l-.12.015c-.345.045-.72.09-1.08.06-.51-.03-.915-.195-1.17-.48-.195-.225-.33-.675-.12-1.095l.06-.1c.15-.21.33-.42.585-.585.075-.06.3-.21.585-.39 1.02-.675 2.085-1.65 3.195-2.88.21-.225.405-.525.375-.855-.03-.3-.255-.465-.465-.54a4.4 4.4 0 00-.405-.105 4.5 4.5 0 01-.63-.15c-.525-.18-.99-.525-1.11-1.02-.075-.36.03-.765.36-.975.165-.12.36-.18.57-.18.15 0 .294.03.42.09.264.116.622.22.922.236.197 0 .325-.045.4-.09a25 25 0 01-.032-.57c-.105-1.628-.232-3.654.297-4.846C7.459 1.069 10.814.793 11.804.793h.401z" /></svg>
            </a>
          )}
          {influencer.twitter && (
            <a href={influencer.twitter} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-[#1DA1F2] hover:bg-[#1DA1F2]/10 transition-all" data-testid={`influencer-tw-${influencer.id}`}>
              <Twitter size={15} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function InfluencersPage() {
  const settings = useSettings();
  const [influencers, setInfluencers] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/influencers?active_only=true`).then(r => setInfluencers(r.data)).catch(() => {});
  }, []);

  const categories = [...new Set(influencers.map(i => i.category).filter(Boolean))];
  const filtered = filter === "all" ? influencers : influencers.filter(i => i.category === filter);

  return (
    <div data-testid="influencers-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-primary/10 rounded-full blur-[120px]" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-block px-4 py-1.5 rounded-full border border-primary/30 text-primary text-sm font-medium mb-4">صناع المحتوى</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Zain'] text-white leading-tight mb-4">
              نجوم <span className="text-primary">المحتوى</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              مؤثرون وصناع محتوى مبدعون نفخر بالتعاون معهم لإيصال رسائل مؤثرة وإبداعية.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      {categories.length > 1 && (
        <section className="pb-8">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-wrap gap-2 justify-center">
              <button onClick={() => setFilter("all")} className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${filter === "all" ? "bg-primary text-white" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`} data-testid="filter-all">
                الكل ({influencers.length})
              </button>
              {categories.map(cat => (
                <button key={cat} onClick={() => setFilter(cat)} className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${filter === cat ? "bg-primary text-white" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`} data-testid={`filter-${cat}`}>
                  {cat} ({influencers.filter(i => i.category === cat).length})
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((inf, i) => (
              <Reveal key={inf.id} delay={i * 0.06}>
                <InfluencerCard influencer={inf} />
              </Reveal>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-20 text-zinc-500">
              <Users size={48} className="mx-auto mb-4 text-zinc-700" />
              <p>لا يوجد مؤثرون حالياً</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// Export the featured section for HomePage
export function FeaturedInfluencersSection() {
  const [influencers, setInfluencers] = useState([]);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    axios.get(`${API}/influencers?featured_only=true`).then(r => setInfluencers(r.data)).catch(() => {});
  }, []);

  if (influencers.length === 0) return null;

  return (
    <section className="py-20 md:py-28" data-testid="featured-influencers-section" ref={ref}>
      <div className="container mx-auto px-4 md:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-12">
          <span className="text-primary text-sm font-medium">صناع المحتوى والمؤثرين</span>
          <h2 className="text-3xl md:text-4xl font-bold font-['Zain'] text-white mt-2">
            نجوم يصنعون <span className="text-primary">الفرق</span>
          </h2>
          <p className="text-zinc-500 text-sm mt-3 max-w-md mx-auto">مؤثرون مبدعون نفخر بالتعاون معهم</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {influencers.slice(0, 8).map((inf, i) => (
            <motion.div key={inf.id} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.08 }}>
              <InfluencerCard influencer={inf} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
